import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import DatalakeService from "src/services/datalake-service"
import dayjs from "dayjs"
import { DATE_FORMAT } from "src/utils/constants"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"
import { formatDecimalCaseReplaceComma } from "src/utils/format-decimal"

const ParameterDatalakeLiquidezTable = (props) => {
    const dataLakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj, index) => ({ ...obj, id: index }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const createRow = async () => {
        const body = {
            "CETIP": "N/A",
            "DataReferencia": dayjs().format(DATE_FORMAT),
            "DataEntrega": dayjs().format(DATE_FORMAT),
            "QtEmitida": 0,
            "QtDias": 0,
            "QtNegocios": 0,
            "QtNegociada": 0,
            "PercQtNegociada": 0,
            "VolumeNegociado": 0,
            "CriterioA": 0,
            "CriterioB": 0,
            "CriterioC": 0,
            "CriterioD": 0,
            "CriterioE": 0,
            "IndiceLiquidez": 0,
            "LOGERRO": "N/A"
        }

        props.loading(true)
        return dataLakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID, CETIP, DataReferencia, QtEmitida, QtDias, QtNegocios, QtNegociada, PercQtNegociada, VolumeNegociado,
            CriterioA, CriterioB, CriterioC, CriterioD, CriterioE, IndiceLiquidez, LOGERRO
        } = newRow

        const body = {
            "CETIP": CETIP,
            "DataReferencia": DataReferencia,
            "QtEmitida": QtEmitida,
            "QtDias": QtDias,
            "QtNegocios": QtNegocios,
            "QtNegociada": QtNegociada,
            "PercQtNegociada": PercQtNegociada,
            "VolumeNegociado": VolumeNegociado,
            "CriterioA": CriterioA,
            "CriterioB": CriterioB,
            "CriterioC": CriterioC,
            "CriterioD": CriterioD,
            "CriterioE": CriterioE,
            "IndiceLiquidez": IndiceLiquidez,
            "LOGERRO": LOGERRO
        }

        props.loading(true)
        return dataLakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        const { ID } = row
        return dataLakeService.deleteTableEndpoint(props.endpoint, ID).then(() => {
            props.loading(false)
            const listRemoved = data.filter((item) => item.ID !== ID)
            setData(listRemoved)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        dataLakeService.paginate(endpoint).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData([])
            setData(list)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80,
        },
        {
            field: 'CETIP',
            headerName: 'CETIP',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'DataReferencia',
            headerName: 'DataReferencia',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'date',
            // valueGetter: ({ value }) => value && dayjs(value).toDate(),
            // valueFormatter: params => dayjs(params.value).format(DATE_FORMAT),
            // valueParser: (newValue) => dayjs(newValue).format(DATE_FORMAT),
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'QtEmitida',
            headerName: 'QtEmitida',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'QtDias',
            headerName: 'QtDias',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'QtNegocios',
            headerName: 'QtNegocios',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'QtNegociada',
            headerName: 'QtNegociada',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'PercQtNegociada',
            headerName: 'PercQtNegociada',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'VolumeNegociado',
            headerName: 'VolumeNegociado',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            renderCell: (params) => formatDecimalCaseReplaceComma(params.value),
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriterioA',
            headerName: 'CriterioA',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriterioB',
            headerName: 'CriterioB',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriterioC',
            headerName: 'CriterioC',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriterioD',
            headerName: 'CriterioD',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriterioE',
            headerName: 'CriterioE',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'IndiceLiquidez',
            headerName: 'IndiceLiquidez',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'LOGERRO',
            headerName: 'LOGERRO',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                filterFunction={onFilter}
                pagination={pagination}
                onChangePage={changePage}
                customFilter={true} />
        </div>
    )
}

ParameterDatalakeLiquidezTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
}

export default ParameterDatalakeLiquidezTable
